<script lang="ts" setup>
import { useUserStore } from "@/stores/user";
import { defineEmits, defineProps, ref } from "vue";
import router from "@/router";
import { useTranslate } from "@/composables/useTranslate";

type Props = {
  isTodayNavigationActive: boolean;
  todayDatePicker: boolean;
  isWeekNavigationActive: boolean;
  weekDatePicker: boolean;
  selectedDate: Date;
  selectedStartDate: Date;
  selectedEndDate: Date;
  searchQuery: string;
  isLoading: boolean;
  activeFilter: string;
};

defineProps<Props>();

type Emits = {
  (event: "goToPreviousDay"): void;
  (event: "goToNextDay"): void;
  (event: "resetToToday"): void;
  (event: "goToPreviousWeek"): void;
  (event: "goToNextWeek"): void;
  (event: "resetToThisWeek"): void;
  (event: "getAllProjects"): void;
  (event: "toggleFilterMenu"): void;
  (event: "setActiveFilter", filter: string): void;
  (event: "onChangeSearch", searchQuery: string): void;
};
defineEmits<Emits>();

const isSearchFocused = ref(false);
const logout = () => {
  const userStore = useUserStore();
  userStore.logout();
  router.push("/login");
};
const { t } = useTranslate();
</script>
<template>
  <div class="flex flex-col gap-3 w-full pb-3 pt-5">
    <!-- Search -->
    <form
      class="w-full h-[40px] rounded-primary-radius px-[10px] border border-[#A7A5AE] bg-transparent flex items-center gap-1 mb-3"
      :class="`${isSearchFocused ? 'bg-white ' : ''}`"
    >
      <div>
        <img class="w-4 h-4" src="@/assets/search-redesign.svg" alt="Search" />
      </div>
      <input
        class="w-full bg-transparent outline-none text-sm font-normal text-[#898790] placeholder:text-[#898790]"
        type="text"
        :placeholder="
          isSearchFocused
            ? t('home.project_name').value
            : t('home.search').value
        "
        :value="searchQuery"
        @input="(event:any)=>$emit('onChangeSearch',event.target.value)"
        @focus="isSearchFocused = true"
        @blur="isSearchFocused = false"
      />
    </form>
    <!-- Day Filter -->
    <div class="flex items-center">
      <button
        v-if="!isTodayNavigationActive"
        @click="$emit('setActiveFilter', 'today')"
        id="today"
        class="flex gap-1 w-full items-center p-2 cursor-pointer bg-[#d3d1cd] rounded-md hover:bg-white duration-300"
      >
        <img src="@/assets/Sun.svg" alt="Sun icon" />
        <p class="text-base font-normal text-primary-text-color">
          {{ t("home.today") }}
        </p>
      </button>

      <div v-if="isTodayNavigationActive" class="w-full">
        <div
          v-if="!todayDatePicker"
          class="w-full flex items-center gap-[6px] py-[8px] px-2 rounded-[7px] bg-white"
        >
          <span
            @click.stop="$emit('goToPreviousDay')"
            class="cursor-pointer w-4 h-4 flex items-center justify-center"
          >
            <img src="@/assets/arrow-left.svg" alt="Return arrow" />
          </span>
          <button @click="$emit('setActiveFilter', 'today')">
            <p class="text-base font-normal text-primary-text-color">
              {{ t("home.today") }}
            </p>
          </button>
          <span
            @click.stop="$emit('goToNextDay')"
            class="cursor-pointer w-4 h-4 flex items-center justify-center"
          >
            <img src="@/assets/arrow-right.svg" alt="Return arrow" />
          </span>
        </div>

        <div
          v-if="todayDatePicker"
          class="w-full flex items-center gap-[6px] py-[8px] px-2 rounded-[7px] bg-white"
        >
          <!-- Reset Button -->
          <button @click="$emit('resetToToday')" class="w-4 h-4 rounded">
            <img src="@/assets/return-arrow.svg" alt="Return arrow" />
          </button>

          <!-- Center Text -->
          <button
            @click="$emit('setActiveFilter', 'today')"
            id="today"
            class="flex cursor-pointer"
          >
            <span>
              {{
                selectedDate.toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                })
              }}
            </span>
          </button>

          <!-- Left Arrow -->
          <span
            @click.stop="$emit('goToPreviousDay')"
            class="cursor-pointer w-4 h-4 flex items-center justify-center"
          >
            <img src="@/assets/arrow-left.svg" alt="Return arrow" />
          </span>

          <!-- Right Arrow -->
          <span
            @click.stop="$emit('goToNextDay')"
            class="cursor-pointer w-4 h-4 flex items-center justify-center"
          >
            <img src="@/assets/arrow-right.svg" alt="Return arrow" />
          </span>
        </div>
      </div>
    </div>
    <!-- Week Filter -->
    <div class="flex items-center w-full">
      <button
        v-if="!isWeekNavigationActive"
        @click="$emit('setActiveFilter', 'week')"
        id="week"
        class="w-full flex gap-1 items-center p-2 cursor-pointer bg-[#d3d1cd] rounded-md hover:bg-white duration-300"
      >
        <img src="@/assets/Calendar_Week.svg" alt="Sun icon" />
        <p class="text-base font-normal text-primary-text-color">
          {{ t("home.this_week") }}
        </p>
      </button>

      <div v-if="isWeekNavigationActive" class="w-full">
        <div
          v-if="!weekDatePicker"
          class="w-full flex items-center gap-[6px] py-[8px] px-2 rounded-[7px] bg-white"
        >
          <span
            @click.stop="$emit('goToPreviousWeek')"
            class="cursor-pointer w-4 h-4 flex items-center justify-center"
          >
            <img src="@/assets/arrow-left.svg" alt="Return arrow" />
          </span>
          <button @click="$emit('setActiveFilter', 'week')">
            <p class="text-base font-normal text-primary-text-color">
              {{ t("home.this_week") }}
            </p>
          </button>
          <span
            @click.stop="$emit('goToNextWeek')"
            class="cursor-pointer w-4 h-4 flex items-center justify-center"
          >
            <img src="@/assets/arrow-right.svg" alt="Return arrow" />
          </span>
        </div>

        <div
          v-if="weekDatePicker"
          class="flex items-center gap-[6px] py-[8px] px-2 rounded-[7px] bg-white"
        >
          <!-- Reset Button -->
          <button @click="$emit('resetToThisWeek')" class="w-4 h-4 rounded">
            <img src="@/assets/return-arrow.svg" alt="Return arrow" />
          </button>

          <!-- Center Text -->
          <button
            @click="$emit('setActiveFilter', 'week')"
            id="week"
            class="flex cursor-pointer"
          >
            <span class="text-base">
              {{
                selectedStartDate.toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                })
              }}
              -
              {{
                selectedEndDate.toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                })
              }}
            </span>
          </button>

          <!-- Left Arrow -->
          <span
            @click.stop="$emit('goToPreviousWeek')"
            class="cursor-pointer w-4 h-4 flex items-center justify-center"
          >
            <img src="@/assets/arrow-left.svg" alt="Return arrow" />
          </span>

          <!-- Right Arrow -->
          <span
            @click.stop="$emit('goToNextWeek')"
            class="cursor-pointer w-4 h-4 flex items-center justify-center"
          >
            <img src="@/assets/arrow-right.svg" alt="Return arrow" />
          </span>
        </div>
      </div>
    </div>
    <!-- Abnahme Filter -->
    <button
      @click="$emit('setActiveFilter', 'abnahme')"
      id="abnahme"
      class="flex gap-1 items-center p-2 cursor-pointer rounded-md hover:bg-white duration-300"
      :class="activeFilter === 'abnahme' ? 'bg-white' : 'bg-[#d3d1cd]'"
    >
      <img src="@/assets/File_Check.svg" alt="Sun icon" />
      <p class="text-base font-normal text-primary-text-color">
        {{ t("home.abnahme_ready") }}
      </p>
    </button>
    <!-- QC Filter -->
    <button
      @click="$emit('setActiveFilter', 'qc')"
      id="qc"
      class="flex gap-1 items-center p-2 cursor-pointer rounded-md hover:bg-white duration-300"
      :class="activeFilter === 'qc' ? 'bg-white' : 'bg-[#d3d1cd]'"
    >
      <img src="@/assets/Clipboard_Check.svg" alt="Sun icon" />
      <p class="text-base font-normal text-primary-text-color">
        {{ t("home.qc_ready") }}
      </p>
    </button>
    <button
      @click="logout"
      id="qc"
      class="flex gap-1 items-center p-2 cursor-pointer rounded-md hover:bg-white duration-300 mt-5"
    >
      <img src="@/assets/Log_Out.svg" alt="Sun icon" />
      <p class="text-base font-normal text-primary-text-color">
        {{ t("global.log_out") }}
      </p>
    </button>
  </div>
</template>
